import React, { useState, useEffect } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
  Tooltip,
  Container
} from '@mui/material';
import {
  Add,
  Upload,
  Download,
  Edit,
  DeleteOutline,
  Visibility,
  VisibilityOff,
  CloudUpload,
  Api as ApiIcon
} from '@mui/icons-material';
import SwaggerUI from 'swagger-ui-react';
import 'swagger-ui-react/swagger-ui.css';
import AdminDocumentation from './AdminDocumentation';
import UserManagement from './UserManagement';
import { API_BASE } from '../config';

// Attaches the stored JWT so admin-only write endpoints accept the request
function authHeader() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function SwaggerManager() {
  const [categories, setCategories] = useState([]);
  const [deleteCategoryDialog, setDeleteCategoryDialog] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState('');
  
  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    try {
      const response = await fetch(`${API_BASE}/swagger-categories/${id}`, { method: 'DELETE', headers: { ...authHeader() } });
      if (response.ok) {
        showAlert('Category deleted successfully');
        fetchCategories();
        // Optionally, remove documents in this category
        fetchDocuments();
      } else {
        const errorData = await response.json();
        showAlert(errorData.error || 'Failed to delete category', 'error');
      }
    } catch (error) {
      showAlert('Error deleting category: ' + error.message, 'error');
    }
  };
  const [selectedCategory, setSelectedCategory] = useState('');
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [createCategoryDialog, setCreateCategoryDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [editDoc, setEditDoc] = useState(null);
  const [newDoc, setNewDoc] = useState({
    name: '',
    description: '',
    category: '',
    content: null
  });
  const [newCategory, setNewCategory] = useState({
    id: '',
    label: '',
    color: 'primary'
  });
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchDocuments();
    fetchCategories();
  }, []);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/swagger-documents`);
      if (response.ok) {
        const docs = await response.json();
        setDocuments(docs);
      }
    } catch (error) {
      showAlert('Failed to fetch documents', 'error');
    }
    setLoading(false);
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE}/swagger-categories`);
      if (response.ok) {
        const cats = await response.json();
        // Transform backend data to match frontend expectations
        const transformedCategories = cats.map(cat => ({
          id: cat.id,
          label: cat.label,
          color: cat.color || 'primary'
        }));
        setCategories(transformedCategories);
        
        // Set first category as selected if none is selected
        if (transformedCategories.length > 0 && !selectedCategory) {
          setSelectedCategory(transformedCategories[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const showAlert = (message, severity = 'success') => {
    setAlert({ show: true, message, severity });
    setTimeout(() => setAlert({ show: false, message: '', severity: 'success' }), 4000);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const content = await file.text();
      const jsonContent = JSON.parse(content);
      
      setNewDoc(prev => ({
        ...prev,
        name: file.name.replace('.json', ''),
        content: jsonContent
      }));
    } catch (error) {
      showAlert('Invalid JSON file', 'error');
    }
  };

  const handleCreateDocument = async () => {
    setUploadError('');
    
    if (!newDoc.name) {
      setUploadError('Document name is required');
      return;
    }

    if (!newDoc.category) {
      setUploadError('Please select a category');
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/swagger-documents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({
          ...newDoc,
          content: newDoc.content || {
            openapi: '3.0.0',
            info: { title: newDoc.name, version: '1.0.0' },
            paths: {}
          }
        })
      });

      if (response.ok) {
        showAlert('Document created successfully');
        fetchDocuments();
        setUploadDialog(false);
        setUploadError('');
        setNewDoc({ name: '', description: '', category: '', content: null });
      } else {
        setUploadError('Failed to create document');
      }
    } catch (error) {
      setUploadError('Error creating document: ' + error.message);
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategory.label || !newCategory.id) {
      showAlert('Category name and ID are required', 'error');
      return;
    }

    // Check if category already exists
    if (categories.some(cat => cat.id === newCategory.id)) {
      showAlert('Category with this ID already exists', 'error');
      return;
    }

    try {
      console.log('Creating category:', newCategory);
      const response = await fetch(`${API_BASE}/swagger-categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(newCategory)
      });

      if (response.ok) {
        const createdCategory = await response.json();
        console.log('Category created successfully:', createdCategory);
        setCategories(prev => [...prev, createdCategory]);
        showAlert('Category created successfully');
        setCreateCategoryDialog(false);
        setNewCategory({ id: '', label: '', color: 'primary' });
      } else {
        const errorData = await response.json();
        console.error('Failed to create category:', errorData);
        showAlert(errorData.error || 'Failed to create category', 'error');
      }
    } catch (error) {
      console.error('Error creating category:', error);
      showAlert('Failed to create category: ' + error.message, 'error');
    }
  };

  const handleEditDocument = (doc) => {
    setEditDoc({ ...doc });
    setEditDialog(true);
  };

  const handleSaveEdit = async () => {
    try {
      const response = await fetch(`${API_BASE}/swagger-documents/${editDoc.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(editDoc)
      });

      if (response.ok) {
        showAlert('Document updated successfully');
        fetchDocuments();
        setEditDialog(false);
      } else {
        showAlert('Failed to update document', 'error');
      }
    } catch (error) {
      showAlert('Error updating document', 'error');
    }
  };

  const handleDeleteDocument = async (id) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      const response = await fetch(`${API_BASE}/swagger-documents/${id}`, {
        method: 'DELETE',
        headers: { ...authHeader() }
      });

      if (response.ok) {
        showAlert('Document deleted successfully');
        fetchDocuments();
      } else {
        showAlert('Failed to delete document', 'error');
      }
    } catch (error) {
      showAlert('Error deleting document', 'error');
    }
  };

  const downloadDocument = (doc) => {
    const blob = new Blob([JSON.stringify(doc.content, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${doc.name}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const currentCategory = categories.find(cat => cat.id === selectedCategory);
  const currentCategoryDocs = documents.filter(doc => doc.category === selectedCategory);

  return (
    <Box sx={{ p: 3 }}>
      {alert.show && (
        <Alert severity={alert.severity} sx={{ mb: 2 }}>
          {alert.message}
        </Alert>
      )}

      <Typography variant="h4" gutterBottom>
        Swagger Document Manager
      </Typography>

      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateCategoryDialog(true)}
        >
          Add API Category
        </Button>
        <Button
          variant="contained"
          color="success"
          startIcon={<CloudUpload />}
          onClick={() => { setUploadError(''); setUploadDialog(true); }}
        >
          Upload JSON
        </Button>
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteOutline />}
          onClick={() => setDeleteCategoryDialog(true)}
          disabled={!selectedCategory}
        >
          Delete API Category
        </Button>
      {/* Delete Category Dialog */}
      <Dialog open={deleteCategoryDialog} onClose={() => setDeleteCategoryDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete API Category</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Select Category</InputLabel>
            <Select
              value={categoryToDelete}
              label="Select Category"
              onChange={e => setCategoryToDelete(e.target.value)}
            >
              {categories.map(cat => (
                <MenuItem key={cat.id} value={cat.id}>{cat.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteCategoryDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteOutline />}
            disabled={!categoryToDelete}
            onClick={() => {
              handleDeleteCategory(categoryToDelete);
              setDeleteCategoryDialog(false);
              setCategoryToDelete('');
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      </Box>

      {categories.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center', mb: 3 }}>
          <Typography variant="h5" color="text.secondary" gutterBottom>
            No API Categories Yet
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Get started by creating your first API category. Categories help organize your Swagger documentation.
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateCategoryDialog(true)}
            size="large"
          >
            Create First Category
          </Button>
        </Paper>
      ) : (
        <>
          <Tabs
            value={selectedCategory}
            onChange={(_, newValue) => setSelectedCategory(newValue)}
            sx={{ mb: 3 }}
          >
            {categories.map((category) => (
              <Tab
                key={category.id}
                value={category.id}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {category.label}
                    <Chip 
                      label={documents.filter(doc => doc.category === category.id).length}
                      size="small"
                      color={category.color}
                    />
                  </Box>
                }
              />
            ))}
          </Tabs>

          {loading ? (
            <CircularProgress />
          ) : currentCategoryDocs.length === 0 ? (
            <Typography>No documents found for {currentCategory?.label}</Typography>
          ) : (
            <Grid container spacing={3}>
              {currentCategoryDocs.map((doc) => (
                <Grid item xs={12} md={6} lg={4} key={doc.id}>
                  <Card>
                    <CardContent>
                  <Typography variant="h6">{doc.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {doc.description || 'No description'}
                  </Typography>
                  <Chip label={doc.category} size="small" />
                </CardContent>
                <CardActions>
                  <IconButton onClick={() => { setSelectedDoc(doc); setViewDialog(true); }}>
                    <Visibility />
                  </IconButton>
                  <IconButton onClick={() => handleEditDocument(doc)}>
                    <Edit />
                  </IconButton>
                  <IconButton onClick={() => downloadDocument(doc)}>
                    <Download />
                  </IconButton>
                  <IconButton onClick={() => handleDeleteDocument(doc.id)} color="error">
                    <DeleteOutline />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
        </>
      )}

      {/* Create Category Dialog */}
      <Dialog open={createCategoryDialog} onClose={() => setCreateCategoryDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New API Category</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Category ID (e.g., PAYMENT_API)"
            value={newCategory.id}
            onChange={(e) => setNewCategory(prev => ({ ...prev, id: e.target.value.toUpperCase().replace(/\s+/g, '_') }))}
            margin="normal"
            helperText="This will be used internally (uppercase, underscores)"
          />
          <TextField
            fullWidth
            label="Category Display Name"
            value={newCategory.label}
            onChange={(e) => setNewCategory(prev => ({ ...prev, label: e.target.value }))}
            margin="normal"
            helperText="This will be shown to users"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Color Theme</InputLabel>
            <Select
              value={newCategory.color}
              onChange={(e) => setNewCategory(prev => ({ ...prev, color: e.target.value }))}
              label="Color Theme"
            >
              <MenuItem value="primary">Blue</MenuItem>
              <MenuItem value="secondary">Purple</MenuItem>
              <MenuItem value="success">Green</MenuItem>
              <MenuItem value="warning">Orange</MenuItem>
              <MenuItem value="error">Red</MenuItem>
              <MenuItem value="info">Cyan</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateCategoryDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateCategory} variant="contained">Add Category</Button>
        </DialogActions>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={uploadDialog} onClose={() => { setUploadDialog(false); setUploadError(''); }} maxWidth="md" fullWidth sx={{ zIndex: 1300 }}>
        <DialogTitle>Upload Swagger JSON Document</DialogTitle>
        <DialogContent>
          {uploadError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError('')}>{uploadError}</Alert>}
          <FormControl fullWidth margin="normal">
            <InputLabel>Category *</InputLabel>
            <Select
              value={newDoc.category}
              onChange={(e) => setNewDoc(prev => ({ ...prev, category: e.target.value }))}
              label="Category *"
              required
            >
              {categories.map(cat => (
                <MenuItem key={cat.id} value={cat.id}>{cat.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            component="label"
            fullWidth
            startIcon={<CloudUpload />}
            sx={{ mt: 2, mb: 2 }}
          >
            Choose JSON File
            <input
              type="file"
              accept=".json"
              hidden
              onChange={handleFileUpload}
            />
          </Button>

          {newDoc.content && (
            <Alert severity="success" sx={{ mb: 2 }}>
              ✓ File selected: {newDoc.name}.json
            </Alert>
          )}

          <TextField
            fullWidth
            label="Document Name *"
            value={newDoc.name}
            onChange={(e) => setNewDoc(prev => ({ ...prev, name: e.target.value }))}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Description"
            value={newDoc.description}
            onChange={(e) => setNewDoc(prev => ({ ...prev, description: e.target.value }))}
            margin="normal"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setUploadDialog(false); setUploadError(''); }}>Cancel</Button>
          <Button onClick={handleCreateDocument} variant="contained" color="success">
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialog} onClose={() => setViewDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>{selectedDoc?.name}</DialogTitle>
        <DialogContent>
          {selectedDoc && (
            <SwaggerUI spec={selectedDoc.content || { openapi: '3.0.0', info: { title: 'API', version: '1.0.0' }, paths: {} }} />
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Document</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Document Name"
            value={editDoc?.name || ''}
            onChange={(e) => setEditDoc(prev => ({ ...prev, name: e.target.value }))}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Description"
            value={editDoc?.description || ''}
            onChange={(e) => setEditDoc(prev => ({ ...prev, description: e.target.value }))}
            margin="normal"
            multiline
            rows={3}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Category</InputLabel>
            <Select
              value={editDoc?.category || ''}
              onChange={(e) => setEditDoc(prev => ({ ...prev, category: e.target.value }))}
              label="Category"
            >
              {categories.map(cat => (
                <MenuItem key={cat.id} value={cat.id}>{cat.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default function AdminPanel() {
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ width: '100%', minHeight: '100vh' }}>
      {/* Modern Admin Header */}
      <Paper 
        elevation={0} 
        sx={{ 
          background: 'linear-gradient(135deg, #134e5e 0%, #71b280 100%)',
          color: 'white',
          p: 2,
          mb: 3,
          borderRadius: 0,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-50%',
            right: '-10%',
            width: '500px',
            height: '500px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            zIndex: 0
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: '-30%',
            left: '-5%',
            width: '400px',
            height: '400px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.08)',
            zIndex: 0
          }
        }}
      >
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              background: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.5rem',
              boxShadow: '0 4px 16px rgba(0,0,0,0.2)'
            }}>
              ⚙️
            </Box>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                Admin Panel
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Manage documentation, API specifications, users, and release notes
              </Typography>
            </Box>
          </Box>
        </Container>
      </Paper>

      <Container maxWidth="xl">
        <Paper elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
          <Tabs 
            value={tab} 
            onChange={(_, v) => setTab(v)} 
            variant="fullWidth"
            sx={{ 
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                minHeight: 64,
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.04)'
                }
              },
              '& .Mui-selected': {
                color: '#134e5e'
              },
              '& .MuiTabs-indicator': {
                height: 3,
                background: 'linear-gradient(90deg, #134e5e 0%, #71b280 100%)'
              }
            }}
          >
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span style={{ fontSize: '1.2rem' }}>📄</span>
                  <span>Documentation</span>
                </Box>
              }
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span style={{ fontSize: '1.2rem' }}>🔌</span>
                  <span>Swagger Documents</span>
                </Box>
              }
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span style={{ fontSize: '1.2rem' }}>👥</span>
                  <span>User Management</span>
                </Box>
              }
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span style={{ fontSize: '1.2rem' }}>📝</span>
                  <span>Release Notes</span>
                </Box>
              }
            />
          </Tabs>
        </Paper>

        {tab === 0 && <AdminDocumentation />}

        {tab === 1 && <SwaggerManager />}

        {tab === 2 && <UserManagement />}

        {tab === 3 && (
          <Box sx={{ p: 2 }}>
            <Typography variant="h5" gutterBottom>Release Notes</Typography>
            <Typography>Release notes and change logs will be displayed here.</Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
}
